import Card from "@/components/card"
import { t } from "i18next"
import { faBuilding, faPen, faTrash, faRightToBracket } from "@fortawesome/free-solid-svg-icons"
import Space from "@/components/space"
import { store, useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "../model/establishment.store"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@/components/button"
import { Establishment } from "../model/establishment.types"
import { User } from "@/redux/user.types"
import { useState } from "react"
import AdmSendEmailModal from "@/modals/adm-send-email.modal"
import Tooltip from "@/components/tooltip"
import { useNavigate } from "react-router-dom"
import AdmEditUserModal from "@/modals/adm-edit-user.modal"
import { sessionEdit } from "@/redux/_session.actions"
import EstablishmentContributors from "./EstablishmentContributors"
import { STATUS_LOADED } from "@/redux/_status.types"

const MODAL_SEND_EMAIL = "MODAL_SEND_EMAIL"
const MODAL_USER_EDIT = "MODAL_USER_EDIT"

interface Props {
  onDelete?: (open: boolean) => void
  onEdit: (establishment: Establishment) => void
  showConnectButton?: boolean
  establishment: Establishment
}

export default function EstablishmentCard({ onDelete, onEdit, showConnectButton, establishment }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const status = useAppSelector(establishmentStore.selectors.status)
  const establishments = useAppSelector(establishmentStore.selectors.list)

  const [modal, setModal] = useState<null | string>(null)
  const [currentUser, setCurrentUser] = useState(new User())

  const handleDelete = (id: string) => {
    if (onDelete) {
      const establishment = establishments.find((e) => e.id === id)
      dispatch(establishmentStore.actions.setActive(establishment))
      if (!establishment) return
      dispatch(establishmentStore.actions.select(establishment.id))
      onDelete(true)
    }
  }

  function enterEstablishmentContext(establishment: Establishment) {
    dispatch(establishmentStore.actions.setActive(establishment))
    store.dispatch(sessionEdit("establishmentId", establishment.id))
    store.dispatch(sessionEdit("risksInterfaceType", "ESTABLISHMENT"))
    navigate("/home")
  }

  function InfoBlock({ label, children }: { label: string; children: React.ReactNode }) {
    return (
      <div className="flex flex-col">
        <div className="text-input-title flex">
          <span className="grey-t">{label}</span>
        </div>
        <div>{children}</div>
      </div>
    )
  }

  return (
    <>
      {modal === MODAL_USER_EDIT && (
        <AdmEditUserModal
          isSmall
          userToUpdate={currentUser}
          onClose={() => setModal(null)}
          onDelete={() => setModal(null)}
          onNext={(user) => {
            setCurrentUser(user)
            setModal(null)
          }}
        />
      )}

      {modal === MODAL_USER_EDIT && (
        <AdmEditUserModal
          isSmall
          userToUpdate={currentUser}
          onClose={() => setModal(null)}
          onDelete={() => setModal(null)}
          onNext={(user) => {
            setCurrentUser(user)
            setModal(null)
          }}
        />
      )}

      {modal === MODAL_SEND_EMAIL && (
        <AdmSendEmailModal
          emailCode="inviteUnitContributor"
          currentUser={currentUser}
          onClose={() => setModal(null)}
          textBold={`Souhaitez-vous envoyer un email d'invitation à ${currentUser.username} (${currentUser.email}) en tant que contributeur du document unique pour l'unité de travail ${establishment.name} ?`}
          items={[{ id: "unit", value: establishment.name }]}
        />
      )}

      <Card status={status !== STATUS_LOADED ? status : undefined}>
        <div className="flex ">
          <div className="my-auto text-2xl">
            <FontAwesomeIcon icon={faBuilding} className="mr-4 grey-t" />
            <span>{establishment.name}</span>
          </div>
          <Space />

          {onDelete && (
            <Tooltip text={`Supprimer l'établissement ${establishment.name}`} position="bottom">
              <Button onClick={() => handleDelete(establishment.id)} icon={faTrash} isWithBorder>
                {t("utils_delete")}
              </Button>
            </Tooltip>
          )}

          <Tooltip text={`Modifier l'établissement ${establishment.name}`} position="bottom">
            <Button onClick={() => onEdit(establishment)} icon={faPen} isWithBorder>
              {t("utils_edit")}
            </Button>
          </Tooltip>

          {showConnectButton && (
            <Tooltip text={`Se connecter à l'établissement ${establishment.name}`} position="bottom">
              <Button
                className="secondary"
                onClick={() => enterEstablishmentContext(establishment)}
                icon={faRightToBracket}
                isWithBorder>
                Se connecter
              </Button>
            </Tooltip>
          )}
        </div>
        <div className="flex gap-12 mb-40">
          <InfoBlock label={t("N° SIRET")}>
            {establishment.establishmentId ? establishment.establishmentId : t("Non renseigné")}
          </InfoBlock>
          <InfoBlock label={t("Code NAF")}>
            {establishment.industryCode ? establishment.industryCode : t("Non renseigné")}
          </InfoBlock>
          <InfoBlock label={t("Adresse")}>
            {establishment.address ? establishment.address : t("Non renseigné")}
          </InfoBlock>
        </div>

        <EstablishmentContributors establishment={establishment} />

        <div className="h-20" />
      </Card>
    </>
  )
}
